import { GUID } from "@faro-lotv/foundation";
import { IElementType } from "@faro-lotv/ielement-types";
import { clearStore } from "@faro-lotv/project-source";
import { RegularImageSheetInfo } from "@faro-lotv/service-wires";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";

/** The common parameters for a sheet mutation creation */
type AreaImgSheetParameterBase = {
  /** image pixel width */
  imageWidth: number;
  /** image pixel height */
  imageHeight: number;
  /** id of the area section to which the sheet will be added */
  areaId: string;
  /** image file name */
  fileName: string;
  /** image file size */
  fileSize: number;
  /** name of the image sheet element to be created */
  name: string;
  /** Url to the new file */
  downloadUrl: string;
  /** Url to the additionally uploaded file */
  additionalUrl: string;
  /** Unique string used to differentiate the created elements */
  md5Hash: string;
  /** Selected PDF page number if the image sheet is created from a PDF file */
  selectedPdfPage?: number;
};

/** The parameters for mutation creation for a regular image sheet */
export type AreaImgSheetParameterRegularSheet = AreaImgSheetParameterBase & {
  /** Type of image sheet to be added */
  elementType: IElementType.imgSheet;
};

/** The parameters for mutation creation for a tiled image sheet */
export type AreaImgSheetParameterTiledSheet = AreaImgSheetParameterBase & {
  /** Type of image sheet to be added */
  elementType: IElementType.imgSheetTiled;

  /**
   * Information about the regular image associated to the tiled image sheet.
   * This is field is for backward compatibility as it ensure that clients of Project API that could not handle tiled image sheets
   * will still be able to access the regular image information.
   */
  associatedRegularImageSheetInfo: RegularImageSheetInfo;
};

/** The parameters for mutation creation for an image sheet */
export type AreaImgSheetParameter =
  | AreaImgSheetParameterRegularSheet
  | AreaImgSheetParameterTiledSheet;

/** State managing data exchange in import sheet session */
type ImportSheetToolState = {
  /**
   * ID of area to which new sheets should be uploaded.
   * If undefined, a new area section will be created for each sheet.
   */
  importSheetAreaId?: GUID;

  /** the number of sheets to import */
  numberOfSheets: number;

  /** parameters used to create multiple mutations to add image sheets and area sections */
  areaImgSheets: AreaImgSheetParameter[];
};

const initialState: ImportSheetToolState = {
  numberOfSheets: 0,
  areaImgSheets: [],
};

const importSheetModeSlice = createSlice({
  initialState,
  name: "importSheetMode",
  reducers: {
    setImportSheetAreaId(state, action: PayloadAction<GUID>) {
      state.importSheetAreaId = action.payload;
    },

    setNumberOfSheets(state, action: PayloadAction<number>) {
      state.numberOfSheets = action.payload;
    },

    resetImportSheetAreaId(state) {
      state.importSheetAreaId = undefined;
    },

    addAreaImgSheet(state, action: PayloadAction<AreaImgSheetParameter>) {
      state.areaImgSheets.push({ ...action.payload });
    },

    resetAreaImgSheets(state) {
      state.areaImgSheets = [];
    },
  },

  extraReducers: (builder) => {
    builder.addCase(clearStore, () => initialState);
  },
});

export const {
  setImportSheetAreaId,
  resetImportSheetAreaId,
  setNumberOfSheets,
  addAreaImgSheet,
  resetAreaImgSheets,
} = importSheetModeSlice.actions;

export const importSheetModeReducer = importSheetModeSlice.reducer;

/**
 * @param state Current state
 * @returns ID of area to which new sheets should be uploaded and aligned to (used for sheet-to-sheet alignment)
 */
export function selectImportSheetAreaId(state: RootState): GUID | undefined {
  return state.importSheetMode.importSheetAreaId;
}

/**
 * @param state Current state
 * @returns number of sheets to be uploaded in the import session
 */
export function selectNumberOfSheets(state: RootState): number {
  return state.importSheetMode.numberOfSheets;
}

/**
 * @param state Current state
 * @returns parameters for sheets that are already uploaded
 */
export function selectAreaImgSheets(state: RootState): AreaImgSheetParameter[] {
  return state.importSheetMode.areaImgSheets;
}
